import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HeroSection } from '@/components/landing/HeroSection';
import { VideoExamplesSection } from '@/components/landing/VideoExamplesSection';
import { FeatureBlocksSection } from '@/components/landing/FeatureBlocksSection';
import { WhoItsForSection } from '@/components/landing/WhoItsForSection';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { RecaptchaGate } from '@/components/RecaptchaGate';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { toast } from 'sonner';
import { trackGA, GA_EVENTS } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';

const Index = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoExamplesSectionRef = useRef<HTMLDivElement>(null);
  const [showRecaptchaGate, setShowRecaptchaGate] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [heroVideoUrl, setHeroVideoUrl] = useState<string | null>('https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1765700804984-62b7c5535_AIPromptDirector_CTA1.mp4');
  
  const { executeRecaptcha } = useRecaptcha();

  // Fetch hero video from DB
  useEffect(() => {
    const fetchHeroVideo = async () => {
      const { data } = await supabase
        .from('site_content')
        .select('video_url')
        .eq('section_key', 'hero_main')
        .eq('is_active', true)
        .maybeSingle();
      
      if (data?.video_url) {
        setHeroVideoUrl(data.video_url);
      }
    };
    fetchHeroVideo();
  }, []);

  // Check URL param to auto-open flow
  useEffect(() => {
    if (searchParams.get('startFlow') === 'true') {
      navigate('/create', { replace: true });
    }
  }, [searchParams, navigate]);

  const handleStartCreating = async (ctaLocation?: string) => {
    trackGA(GA_EVENTS.CTA_CLICKED, {
      cta_location: ctaLocation || 'unknown',
      page: 'landing',
    });
    trackGA(GA_EVENTS.FLOW_STARTED, {
      source: 'landing_cta',
      cta_location: ctaLocation || 'unknown',
    });
    
    setIsVerifying(true);
    
    try {
      const result = await executeRecaptcha();
      
      if (result === 'success' || result === 'not_ready') {
        navigate('/create');
      } else {
        setShowRecaptchaGate(true);
      }
    } catch (error) {
      console.error('[Index] ❌ reCAPTCHA error:', error);
      navigate('/create');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRecaptchaSuccess = () => {
    setShowRecaptchaGate(false);
    navigate('/create');
  };

  const handleRecaptchaCancel = () => {
    setShowRecaptchaGate(false);
    toast.info('Verification cancelled');
  };

  const handleViewExamples = () => {
    videoExamplesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>VideoPromptFlow - AI-Powered Video Script & Storyboard Creator</title>
        <meta
          name="description"
          content="Transform your ideas into AI-ready video scripts in minutes. AI-powered Story Director that creates shooting scripts and scene-by-scene prompts for Veo 3 and Powtoon."
        />
      </Helmet>

      <main className="min-h-screen bg-background">
        <HeroSection 
          onStartCreating={() => handleStartCreating('hero')} 
          onViewExamples={handleViewExamples}
          heroVideoUrl={heroVideoUrl}
        />
        
        <div ref={videoExamplesSectionRef}>
          <VideoExamplesSection isPaused={false} />
        </div>
        
        <FeatureBlocksSection onStartCreating={() => handleStartCreating('feature_blocks')} />
        <WhoItsForSection />
        <FinalCTASection onStartCreating={() => handleStartCreating('final_cta')} />
      </main>

      <RecaptchaGate 
        isOpen={showRecaptchaGate}
        onSuccess={handleRecaptchaSuccess}
        onCancel={handleRecaptchaCancel}
      />
    </>
  );
};

export default Index;
